import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useMqttSubscription } from './useMqttSubscription';

export const useMqttDataLogger = (topics, sensorConfigs, maxDataPoints = 500) => {
  const [dataPoints, setDataPoints] = useState([]);
  const [isLogging, setIsLogging] = useState(true);
  const dataBuffer = useRef([]);
  const lastMessageTime = useRef(0);
  const updateTimeoutRef = useRef(null);
  
  // Debug flag - set to true to see detailed logs
  const DEBUG = false;

  const processMessage = useCallback((topic, message, packet) => {
    if (!isLogging) return;
    
    // Update last message timestamp
    lastMessageTime.current = Date.now();

    try {
      if (DEBUG) {
        console.log('------ MQTT Message Received ------');
        console.log('Topic:', topic);
        console.log('Message type:', typeof message);
        console.log('Is Buffer:', Buffer.isBuffer(message));
        console.log('Message length:', message.length);
        
        // Log the first few bytes
        if (message.length > 0) {
          const preview = Buffer.isBuffer(message) ? 
            Array.from(message) : 
            (Array.isArray(message) ? message : Array.from(message));
            
          console.log('First bytes (hex):', preview.slice(0, Math.min(16, preview.length))
            .map(b => b.toString(16).padStart(2, '0')).join(' '));
          console.log('First bytes (dec):', preview.slice(0, Math.min(16, preview.length)));
        }
      }

      // Find matching sensor configuration
      let matchingSensor = null;
      for (const [, config] of Object.entries(sensorConfigs)) {
        if (topic.includes(config.topic)) {
          matchingSensor = config;
          break;
        }
      }

      if (!matchingSensor) {
        console.warn(`No matching sensor configuration found for topic: ${topic}`);
        return;
      }

      // Get data format
      const dataFormat = matchingSensor.dataFormat || {
        dataType: matchingSensor.dataType,
        signed: matchingSensor.signed,
        scaling: matchingSensor.scaling || 1
      };
      
      if (DEBUG) {
        console.log('Matched sensor:', matchingSensor.sensor);
        console.log('Data format:', dataFormat);
      }

      // Ensure we have an array of bytes
      let dataArray;
      
      if (Buffer.isBuffer(message)) {
        dataArray = Array.from(message);
      } else if (Array.isArray(message)) {
        dataArray = message;
      } else if (message instanceof Uint8Array) {
        dataArray = Array.from(message);
      } else {
        console.error('Unsupported message format:', typeof message);
        return;
      }
      
      if (dataArray.length === 0) {
        console.warn('Empty message received');
        return;
      }
      
      if (DEBUG) {
        console.log('Data array length:', dataArray.length);
        console.log('Data type to parse as:', dataFormat.dataType);
      }
      
      // Parse binary data based on data type
      const rawValues = [];
      
      // Parse based on data type (using big-endian byte order)
      switch (dataFormat.dataType) {
        case 'uint16':
        case 'int16':
          // Read 2 bytes at a time, big-endian (MSB first)
          for (let i = 0; i < dataArray.length; i += 2) {
            if (i + 1 < dataArray.length) {
              // Log each byte pair for debugging
              if (DEBUG) {
                console.log(`Bytes at index ${i}: [${dataArray[i]}, ${dataArray[i + 1]}] (${dataArray[i].toString(16).padStart(2, '0')}, ${dataArray[i + 1].toString(16).padStart(2, '0')})`);
              }
              
              // Big-endian: first byte is most significant (MSB), second byte is least significant (LSB)
              let value = (dataArray[i] << 8) | dataArray[i + 1];
              
              // Handle signed conversion if needed
              if (dataFormat.signed && value > 32767) {
                value = value - 65536; // Convert to negative (two's complement)
              }
              
              if (DEBUG) {
                console.log(`Parsed value at index ${i}: ${value}`);
              }
              
              rawValues.push(value);
            }
          }
          break;
          
        case 'uint32':
        case 'int32':
          // Read 4 bytes at a time, big-endian (MSB first)
          for (let i = 0; i < dataArray.length; i += 4) {
            if (i + 3 < dataArray.length) {
              // Log each byte group for debugging
              if (DEBUG) {
                console.log(`Bytes at index ${i}: [${dataArray[i]}, ${dataArray[i + 1]}, ${dataArray[i + 2]}, ${dataArray[i + 3]}]`);
              }
              
              // Big-endian: bytes from most significant to least significant
              let value = (dataArray[i] << 24) | 
                         (dataArray[i + 1] << 16) | 
                         (dataArray[i + 2] << 8) | 
                          dataArray[i + 3];
              
              // Handle signed conversion if needed
              if (dataFormat.signed && value > 2147483647) {
                value = value - 4294967296; // Convert to negative (two's complement)
              }
              
              if (DEBUG) {
                console.log(`Parsed value at index ${i}: ${value}`);
              }
              
              rawValues.push(value);
            }
          }
          break;
          
        case 'float':
        case 'float32':
          // Read 4 bytes at a time as IEEE 754 float, big-endian
          for (let i = 0; i < dataArray.length; i += 4) {
            if (i + 3 < dataArray.length) {
              // Log each byte group for debugging
              if (DEBUG) {
                console.log(`Float bytes at index ${i}: [${dataArray[i]}, ${dataArray[i + 1]}, ${dataArray[i + 2]}, ${dataArray[i + 3]}]`);
                console.log(`Float bytes (hex): ${dataArray[i].toString(16).padStart(2, '0')} ${dataArray[i+1].toString(16).padStart(2, '0')} ${dataArray[i+2].toString(16).padStart(2, '0')} ${dataArray[i+3].toString(16).padStart(2, '0')}`);
              }
              
              const buffer = new ArrayBuffer(4);
              const view = new DataView(buffer);
              
              // Set bytes in big-endian order (most significant byte first)
              view.setUint8(0, dataArray[i]);     // MSB
              view.setUint8(1, dataArray[i + 1]);
              view.setUint8(2, dataArray[i + 2]);
              view.setUint8(3, dataArray[i + 3]); // LSB
              
              // false parameter = big-endian (most significant byte first)
              const value = view.getFloat32(0, false);
              
              if (DEBUG) {
                console.log(`Parsed float value at index ${i}: ${value}`);
              }
              
              // Check for NaN or Infinity which might indicate parsing issues
              if (!isFinite(value)) {
                console.warn(`Invalid float value at index ${i}`, dataArray.slice(i, i + 4));
                continue;
              }
              
              rawValues.push(value);
            }
          }
          break;
          
        default:
          console.warn(`Unsupported data type: ${dataFormat.dataType} for topic ${topic}`);
          return;
      }

      if (rawValues.length === 0) {
        console.warn('No values could be parsed from binary message', topic);
        return;
      }

      if (DEBUG) {
        console.log('Parsed raw values:', rawValues);
        console.log('Scaling factor:', dataFormat.scaling || 1);
      }

      // Process each value
      rawValues.forEach((rawValue, index) => {
        if (DEBUG) {
          console.log(`Processing value ${index}: raw=${rawValue}`);
        }
      
        const dataPoint = {
          rawValue: rawValue,
          value: rawValue,
          sensor: matchingSensor.sensor,
          topic: matchingSensor.topic,
          mqttTopic: topic,
          dataType: dataFormat.dataType,
        };

        dataBuffer.current.push(dataPoint);
      });

      
      // Limit data points to prevent memory issues
      if (dataBuffer.current.length > maxDataPoints) {
        dataBuffer.current = dataBuffer.current.slice(-maxDataPoints);
      }

      // Debounce state updates to prevent too many re-renders
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      updateTimeoutRef.current = setTimeout(() => {
        setDataPoints([...dataBuffer.current]);
        updateTimeoutRef.current = null;
      }, 100); // Update state max 10 times per second
      
      if (DEBUG) {
        console.log('Total data points stored:', dataBuffer.current.length);
        console.log('------ Message Processing Complete ------');
      }
      
    } catch (error) {
      console.error('Error processing binary MQTT data:', error, 'Topic:', topic);
      
      // Additional error debug information
      console.error('Message details:', {
        type: typeof message,
        length: message?.length,
        isBuffer: Buffer.isBuffer(message),
        isArray: Array.isArray(message)
      });
    }
  }, [isLogging, maxDataPoints, sensorConfigs, DEBUG]);

  // Ensure topics is a valid array with at least one topic
  const validTopics = useMemo(() => {
    if (!topics) return [];
    const topicsArray = Array.isArray(topics) ? topics : [topics];
    return topicsArray.filter(t => t && typeof t === 'string' && t.trim() !== '');
  }, [topics]);

  // Set up MQTT subscription with valid topics
  const { isConnected, reconnect } = useMqttSubscription(validTopics, processMessage);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Monitor for stalled connections
  useEffect(() => {
    // Only set up monitoring if we're actually connected and have received at least one message
    if (!isConnected || lastMessageTime.current === 0) return;
    
    const checkInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastMessage = now - lastMessageTime.current;
      
      if (timeSinceLastMessage > 60000) { // No message for 60 seconds
        console.warn('No messages received for 60 seconds. Attempting to reconnect...');
        reconnect();
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(checkInterval);
  }, [isConnected, reconnect]);

  const clearData = useCallback(() => {
    dataBuffer.current = [];
    setDataPoints([]);
  }, []);

  const toggleLogging = useCallback(() => {
    setIsLogging(prev => !prev);
  }, []);

  return {
    dataPoints,
    isConnected,
    isLogging,
    clearData,
    toggleLogging,
    dataCount: dataPoints.length,
    lastMessageTime: lastMessageTime.current,
    reconnect
  };
};